/* eslint-disable no-param-reassign */
import { createApi } from '@reduxjs/toolkit/query/react';
import isEqual from 'lodash.isequal';
import { networkBaseQueryV2 } from './queries';
import removeEmptyProps from '../helpers/removeEmptyProps';
import deserialize from '../helpers/getroPropertyMapper';

export const hiresV2Api = createApi({
  reducerPath: 'hiresV2Api',
  baseQuery: networkBaseQueryV2,
  tagTypes: ['hires'],
  endpoints: (builder) => ({
    getHiresV2: builder.query({
      query: ({
        keyword,
        hireDateFrom,
        hireDateTo,
        teamInvolvement,
        sortBy,
        sortDirection,
        companyId,
        sources,
        importerIds,
        firstSeenDate,
        page = 1,
        perPage = 20,
      }) => ({
        url: '/hires',
        params: removeEmptyProps({
          keyword,
          hire_date_from: hireDateFrom,
          hire_date_to: hireDateTo,
          team_involvement: teamInvolvement,
          sort_by: sortBy,
          sort_direction: sortDirection,
          company_id: companyId,
          sources,
          importer_ids: importerIds,
          first_seen_date: firstSeenDate,
          page,
          per_page: perPage,
        }),
      }),
      transformResponse: (response) => {
        const deserialized = deserialize(response);

        return {
          items: deserialized.items || deserialized || [],
          meta: response.meta,
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // Exclude 'page' and 'perPage' from cache key so all pages share the same cache entry
        const { page, perPage, ...rest } = queryArgs || {};
        // Sort keys to ensure consistent cache key regardless of property order
        const sortedRest = Object.keys(rest)
          .sort()
          .reduce((acc, key) => {
            acc[key] = rest[key];
            return acc;
          }, {});
        return `${endpointName}(${JSON.stringify(sortedRest)})`;
      },
      merge: (currentCache, incomingState) => {
        // RTK Query always provides currentCache, but ensure items array exists
        if (!currentCache.items) {
          currentCache.items = [];
        }

        if (incomingState && incomingState.items && Array.isArray(incomingState.items)) {
          // Append only new items (deduplicate by id)
          const existingIds = new Set(currentCache.items.map((item) => item.id));
          const newItems = incomingState.items.filter((item) => !existingIds.has(item.id));
          currentCache.items.push(...newItems);
          // Update meta with latest values
          if (incomingState.meta) {
            currentCache.meta = incomingState.meta;
          }
        }
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg) return true;
        // Only refetch if non-page params change, or if going to a new page
        const { page: currentPage, ...currentRest } = currentArg || {};
        const { page: previousPage, ...previousRest } = previousArg || {};

        // If filters changed, refetch
        if (!isEqual(currentRest, previousRest)) return true;

        // If page changed and we don't have that page yet, refetch
        // RTK Query will merge the results
        return currentPage !== previousPage;
      },
      providesTags: ['hires'],
    }),
    exportHires: builder.mutation({
      query: ({
        keyword,
        hireDateFrom,
        hireDateTo,
        teamInvolvement,
        sortBy,
        sortDirection,
        companyId,
        sources,
        importerIds,
        firstSeenDate,
      }) => ({
        url: '/hires/export',
        method: 'post',
        params: removeEmptyProps({
          keyword,
          hire_date_from: hireDateFrom,
          hire_date_to: hireDateTo,
          team_involvement: teamInvolvement,
          sort_by: sortBy,
          sort_direction: sortDirection,
          company_id: companyId,
          sources,
          importer_ids: importerIds,
          first_seen_date: firstSeenDate,
        }),
      }),
    }),
    getHiresCompanies: builder.query({
      query: ({ query }) => ({
        url: '/organizations/search',
        params: removeEmptyProps({
          q: query,
          per_page: 50,
        }),
      }),
      transformResponse: (response) => {
        const organizations = deserialize(response);
        return organizations.map((org) => ({
          value: org.id,
          label: org.name || org.attributes?.name || '',
        }));
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}(${queryArgs?.query || ''})`,
    }),
  }),
});

export const { useGetHiresV2Query, useExportHiresMutation, useLazyGetHiresCompaniesQuery } = hiresV2Api;
